import lodashGet from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { apis, inWindow } from '@/config';
import { api } from '@/api/api';
import { AbstractDBInterfaceImpl } from './db_abs';
import db_tables from '@/api/data/db_tables';
import { personalRegexp } from '@/api/util';
export class DBInterfaceImpl extends AbstractDBInterfaceImpl {
    constructor() {
        super();
        this.name = apis.dbInterfaceApiImpl;
        this.dbApi = api.requireLogicalApi(apis.dexieDbApi);
        this.memoryApi = api.requireLogicalApi(apis.dbMemoryApiImpl);
        this.productApi = api.requireLogicalApi(apis.productAuthApiImpl);
        this.systemApi = api.getSystemApi();
        this.fieldCheckMap = new Map();
        this.fieldCheckMap.set('contact', {
            type(val) {
                return `${val}` === 'personal';
            },
            id(val) {
                return personalRegexp.test(val);
            },
        });
        this.fieldCheckMap.set('contactItem', {
            type(val) {
                return `${val}` === 'personal';
            },
            contactId(val) {
                return personalRegexp.test(val);
            },
        });
        this.fieldCheckMap.set('orgContact', {
            orgId(val) {
                return val.startsWith('personal_org');
            },
            id(val) {
                return val.startsWith('personal_org');
            },
        });
        this.fieldCheckMap.set('org', {
            id(val) {
                return val.startsWith('personal_org');
            },
            type(val) {
                return `${val}` === '2001';
            },
        });
    }
    init() {
        return this.name;
    }
    getShouldUseMemoryDb() {
        return false;
        if (process.env.BUILD_ISPREVIEWPAGE) {
            return false;
        }
        return true;
    }
    afterLoadFinish() {
        return this.name;
    }
    afterLogin() {
        return this.name;
    }
    initDb(name, _account) {
        if (this.getShouldUseMemoryDb()) {
            this.memoryApi.initDb(name, _account);
        }
        return this.dbApi.initDb(name, _account);
    }
    addFilterRegistry(registry) {
        this.memoryApi.addFilterRegistry(registry);
        this.dbApi.addFilterRegistry(registry);
    }
    close() {
        this.dbApi.close();
    }
    closeSpecific(db, _account) {
        this.dbApi.closeSpecific(db, _account);
    }
    useMemory4RangeConditionBak(query) {
        const { dbName, tableName } = query;
        if (inWindow() && !window.isBridgeWorker) {
            return false;
        }
        if (dbName !== 'contact_dexie' || ![...this.fieldCheckMap.keys()].includes(tableName)) {
            return false;
        }
        let flag = false;
        const fieldCheckConfig = this.fieldCheckMap.get(tableName);
        const conditionType = lodashGet(query, 'adCondition.type', '');
        switch (conditionType) {
            case 'anyOf': {
                const args = query.adCondition.args;
                if (!Array.isArray(args) || !args.length) {
                    return false;
                }
                const fieldArr = query.adCondition.field;
                const filed = Array.isArray(fieldArr) ? fieldArr[0] : fieldArr;
                const totalKeys = Object.keys(fieldCheckConfig);
                if (!totalKeys.includes(filed)) {
                    return false;
                }
                flag = args.flat().every(_arg => fieldCheckConfig[filed](_arg));
                break;
            }
            case 'startsWith': {
                const args = `${query.adCondition.args}`;
                const fieldArr = query.adCondition.field;
                const filed = Array.isArray(fieldArr) ? fieldArr[0] : fieldArr;
                const totalKeys = Object.keys(fieldCheckConfig);
                if (!totalKeys.includes(filed)) {
                    return false;
                }
                flag = fieldCheckConfig[filed](args);
                break;
            }
            case 'equals': {
                const args = `${query.adCondition.args}`;
                const fieldArr = query.adCondition.field;
                const filed = Array.isArray(fieldArr) ? fieldArr[0] : fieldArr;
                const totalKeys = Object.keys(fieldCheckConfig);
                if (!totalKeys.includes(filed)) {
                    return false;
                }
                flag = fieldCheckConfig[filed](args);
                break;
            }
            default:
                break;
        }
        return flag;
    }
    useMemory4EqConditionBak(query) {
        const { dbName, tableName } = query;
        if (dbName !== 'contact_dexie' || ![...this.fieldCheckMap.keys()].includes(query.tableName) || !window.isBridgeWorker) {
            return false;
        }
        const fieldCheckConfig = this.fieldCheckMap.get(tableName);
        const fieldCheckKeys = Object.keys(fieldCheckConfig);
        if (!query.query) {
            return false;
        }
        return Object.keys(query.query).every(_queryKey => {
            if (!fieldCheckKeys.includes(_queryKey)) {
                return false;
            }
            const call = fieldCheckConfig[_queryKey];
            return call(`${query.query[_queryKey]}`);
        });
    }
    useMemory4WriteBak(dbRefer, result) {
        const fieldMap = new Map([
            [
                'contact',
                {
                    type(val) {
                        return val === 'personal';
                    },
                },
            ],
            [
                'contactItem',
                {
                    type(val) {
                        return val === 'personal';
                    },
                },
            ],
            [
                'orgContact',
                {
                    orgId(val) {
                        return val.startsWith('personal_org');
                    },
                    id(val) {
                        return val.startsWith('personal_org');
                    },
                },
            ],
            [
                'org',
                {
                    id(val) {
                        return val.startsWith('personal_org');
                    },
                },
            ],
        ]);
        const { dbName, tableName } = dbRefer;
        if (dbName !== 'contact_dexie' || ![...fieldMap.keys()].includes(tableName)) {
            return false;
        }
        return result.every(item => {
            const regField = Object.keys(fieldMap.get(tableName))[0];
            if (!Reflect.has(item, regField)) {
                return false;
            }
            const regCall = fieldMap.get(tableName)[regField];
            return regCall(item[regField]);
        });
    }
    checkLocalSupportType(dbRefer) {
        if (dbRefer.dbName === 'contact_search' || dbRefer.dbName === 'edm_contact_search') {
            return ['loki'];
        }
        const supportTypes = lodashGet(db_tables, `[${dbRefer.dbName}].using`, 'dexie');
        if (!Array.isArray(supportTypes)) {
            return [supportTypes];
        }
        return supportTypes.filter(item => {
            if (item === 'loki') {
                return dbRefer.asSoon;
            }
            return true;
        });
    }
    async getById(table, id, _account) {
        return this.dbApi.getById(table, id, _account);
    }
    async getByIds(table, ids, _account) {
        return this.dbApi.getByIds(table, ids, _account);
    }
    async getByIndexIds(table, keyPath, ids, _account) {
        return this.dbApi.getByIndexIds(table, keyPath, ids, _account);
    }
    async deleteById(db, id, _account) {
        const ids = Array.isArray(id) ? id : [id];
        if (!ids || !ids.length) {
            return;
        }
        const supportTypes = this.checkLocalSupportType(Object.assign({ asSoon: false }, db));
        if (supportTypes.length === 1) {
            this[supportTypes.includes('loki') ? 'memoryApi' : 'dbApi'].deleteById(db, id, _account);
            return;
        }
        this.memoryApi.deleteById(db, id, _account);
        this.dbApi.deleteById(db, id, _account);
    }
    async deleteByByRangeCondition(query, _account, _options) {
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(query, {
            asSoon,
        }));
        if (supportTypes.length === 1) {
            return this[supportTypes.includes('loki') ? 'memoryApi' : 'dbApi'].deleteByByRangeCondition(query, _account);
        }
        this.memoryApi.deleteByByRangeCondition(query, _account);
        return this.dbApi.deleteByByRangeCondition(query, _account);
    }
    async getByEqCondition(query, _account) {
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(query, {
            asSoon,
        }));
        if (supportTypes.length === 1 && supportTypes.includes('loki')) {
            return this.memoryApi.getByEqCondition(query, _account);
        }
        if (supportTypes.length === 1 && supportTypes.includes('dexie')) {
            return this.dbApi.getByEqCondition(query, _account);
        }
        return this.memoryApi.getByEqCondition(query, _account).catch(error => {
            console.warn('[dbInterfaceImpl]getByRangeCondition.lokiError:', error);
            const ret = this.dbApi.getByEqCondition(query, _account);
            return ret;
        });
    }
    async getByRangeCondition(query, _account) {
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(query, {
            asSoon,
        }));
        if (supportTypes.includes('loki')) {
            const list = await this.memoryApi.getByRangeCondition(query, _account);
            return list;
        }
        return this.dbApi.getByRangeCondition(query, _account);
    }
    async getTableCount(table, _account) {
        const supportTypes = this.checkLocalSupportType(table);
        if (supportTypes.length === 1 && supportTypes.includes('loki')) {
            return this.memoryApi.getTableCount(table, _account);
        }
        if (supportTypes.length === 1 && supportTypes.includes('dexie')) {
            return this.dbApi.getTableCount(table, _account);
        }
        return this.memoryApi.getTableCount(table, _account).catch(error => {
            console.warn('[dbInterfaceImpl]getByRangeCondition.lokiError:', error);
            const ret = this.dbApi.getTableCount(table, _account);
            return ret;
        });
    }
    async put(table, data, _account) {
        if (!data) {
            return data;
        }
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(table, {
            asSoon,
        }));
        if (!Array.isArray(supportTypes)) {
            throw new Error('loki or dexie');
        }
        try {
            let ret;
            if (supportTypes.length === 1) {
                ret = await this[supportTypes[0] === 'loki' ? 'memoryApi' : 'dbApi'].put(table, data, _account);
            }
            else {
                this.memoryApi.put(table, cloneDeep(data), _account);
                ret = await this.dbApi.put(table, data, _account);
            }
            if (!ret) {
                console.error('putAll error');
                return ret;
            }
            return ret;
        }
        catch (ex) {
            console.error('[db_interface_impl]put.error', ex, table);
        }
        return data;
    }
    async putAll(table, data, options, _account) {
        if (data.length === 0) {
            return data;
        }
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(table, {
            asSoon,
        }));
        if (!Array.isArray(supportTypes)) {
            throw new Error('loki or dexie');
        }
        let ret = [];
        if (supportTypes.length === 1) {
            ret = await this[supportTypes[0] === 'loki' ? 'memoryApi' : 'dbApi'].putAll(table, data, options, _account);
        }
        else {
            this.memoryApi.putAll(table, cloneDeep(data), undefined, _account);
            ret = await this.dbApi.putAll(table, data, options, _account);
        }
        if (!ret) {
            console.error('putAll error');
            return [];
        }
        return ret;
    }
    async bulkPut(table, data, _account) {
        const asSoon = false;
        const supportTypes = this.checkLocalSupportType(Object.assign(table, {
            asSoon,
        }));
        if (!Array.isArray(supportTypes)) {
            throw new Error('loki or dexie');
        }
        let ret = [];
        if (supportTypes.length === 1) {
            ret = await this[supportTypes[0] === 'loki' ? 'memoryApi' : 'dbApi'].bulkPut(table, data, _account);
        }
        else {
            this.memoryApi.bulkPut(table, cloneDeep(data), _account);
            ret = await this.dbApi.bulkPut(table, cloneDeep(data), _account);
        }
        if (!ret) {
            console.error('putAll error');
            return [];
        }
        return ret;
    }
    deleteDB(dbName, _account) {
        return this.dbApi.deleteDB(dbName, _account);
    }
    removeAccountAction(emailMD5) {
        this.dbApi.removeAccountAction(emailMD5);
    }
    getByEqConditionWithTotal(query, _account) {
        return this.dbApi.getByEqConditionWithTotal ? this.dbApi.getByEqConditionWithTotal(query, _account) : Promise.reject('no getByEqConditionWithTotal');
    }
    getByRangeConditionWithTotalCount(query, _account) {
        return this.dbApi.getByRangeConditionWithTotalCount
            ? this.dbApi.getByRangeConditionWithTotalCount(query, _account)
            : Promise.reject('no getByRangeConditionWithTotalCount');
    }
    async clear(dbRefer, _account) {
        await this.dbApi.clear(dbRefer, _account);
        return true;
    }
    getTotalDatabaseNames(filterDbName) {
        return this.dbApi.getTotalDatabaseNames(filterDbName);
    }
}
const impl = new DBInterfaceImpl();
api.registerLogicalApi(impl);
export default impl;
